/*
 * components.jsx
 *
 * Reusable UI primitives used across the dashboard.
 * No data fetching — pure presentational components that receive all data via props.
 * Exports: SubLabel, Pill, ConfidenceBadge, StatCard, ChargerDot, AlertRow, ChartModal.
 * Imported by: Dashboard.jsx (StatCard, ChargerDot, AlertRow), DetailPanel.jsx (Pill, ConfidenceBadge, ChartModal).
 */

import { TIER_CONFIG, getTier, getConfidence } from "./utils";

export const SubLabel = ({ tier, text }) => {
  const color = { P1: "#EF4444", P2: "#F97316", P3: "#F59E0B", P4: "#0EA5E9", EW: "#8B5CF6", "—": "#9CA3AF" }[tier] || "#9CA3AF";
  const label = (TIER_CONFIG[tier]?.text || tier).toUpperCase();
  return (
    <>
      <span style={{ color, fontWeight: 600 }}>{label}</span>
      {text && <><span style={{ color: "#9CA3AF" }}> | </span>{text}</>}
    </>
  );
};

export const Pill = ({ children, color }) => (
  <span style={{
    fontSize: 10, padding: "2px 8px", borderRadius: 999,
    background: color ? `${color}14` : "#F3F4F6",
    color: color || "#374151",
    border: `1px solid ${color ? `${color}40` : "#E5E7EB"}`,
    fontWeight: 500, whiteSpace: "nowrap",
  }}>{children}</span>
);

export const ConfidenceBadge = ({ prob }) => {
  const c = getConfidence(prob);
  return (
    <span style={{
      fontSize: 9, padding: "1px 5px", borderRadius: 3, fontWeight: 700,
      letterSpacing: "0.05em", background: c.bg, color: c.color,
    }}>{c.label}</span>
  );
};

export const StatCard = ({ label, value, sub, accent, tier, activeFilter, onFilterChange }) => {
  const isActive = activeFilter === tier;
  return (
    <div
      className="dp-stat-card"
      onClick={() => tier && onFilterChange(isActive ? "ALL" : tier)}
      style={{
        background: isActive ? `${accent}14` : "#FFFFFF",
        border: `2px solid ${isActive ? accent : "#E5E7EB"}`,
        borderTop: `4px solid ${accent}`,
        padding: "16px 20px", borderRadius: 8, minWidth: 140,
        cursor: tier ? "pointer" : "default",
        transition: "all 0.15s", userSelect: "none",
        boxShadow: isActive ? `0 0 0 1px ${accent}30` : "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div className="dp-stat-label" style={{ fontSize: 11, color: "#6B7280", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</div>
      {sub && <div className="dp-stat-sub" style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{sub}</div>}
    </div>
  );
};

export const ChargerDot = ({ charger, onClick, selected, activeFilter }) => {
  const tc = getTier(charger.priority_tier);
  const tierKey      = charger.priority_tier?.slice(0, 2);
  const dotColor     = charger.alert_type === "EARLY_WARNING" ? "#8B5CF6" : tc.color;
  const dimmed       = activeFilter !== "ALL" && tierKey !== activeFilter;
  const hasProactive = (charger.bilstm_parts_prob ?? 0) >= 0.50;
  return (
    <div
      onClick={() => onClick(charger)}
      title={[
        charger.station_id && charger.station_id !== "—" ? charger.station_id : charger.charger_sn,
        charger.site_name || "",
        charger.station_id && charger.station_id !== "—" ? charger.charger_sn : "",
        `${charger.priority_tier} · P: ${(charger.fault_probability * 100).toFixed(1)}%`,
        hasProactive ? `🔧 Parts: ${Math.round(charger.bilstm_parts_prob * 100)}%` : "",
      ].filter(Boolean).join("\n")}
      style={{ position: "relative", display: "inline-flex", flexShrink: 0, cursor: "pointer" }}
    >
      <div style={{
        width: 10, height: 10, borderRadius: "50%",
        background: dotColor,
        opacity: dimmed ? 0.12 : selected ? 1 : 0.85,
        boxShadow: selected ? `0 0 0 2px #fff, 0 0 0 4px ${dotColor}` : "none",
        transition: "all 0.15s",
      }} />
      {hasProactive && !dimmed && (
        <span style={{ position: "absolute", top: -5, right: -6, fontSize: 7, lineHeight: 1, pointerEvents: "none" }}>🔧</span>
      )}
    </div>
  );
};

export const AlertRow = ({ charger, onClick, selected }) => {
  const tc = getTier(charger.priority_tier);
  const isEarly    = charger.alert_type === "EARLY_WARNING";
  const badgeColor = isEarly ? "#8B5CF6" : tc.color;
  const badgeBg    = isEarly ? "#F5F3FF" : tc.bg;
  const badgeLabel = isEarly ? "EARLY"   : tc.label;
  return (
    <div
      onClick={() => onClick(charger)}
      className="row-hover"
      style={{
        display: "flex", alignItems: "flex-start", gap: 12,
        padding: "12px 16px",
        background: selected ? "#EFF6FF" : "transparent",
        borderLeft: `3px solid ${selected ? "#1E3A8A" : "transparent"}`,
        cursor: "pointer", transition: "all 0.15s",
        borderBottom: "1px solid #F9FAFB",
      }}
    >
      <div style={{
        background: badgeBg, border: `1px solid ${badgeColor}`,
        color: badgeColor, fontSize: 10, fontWeight: 700,
        padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2,
      }}>{badgeLabel}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 1, flexWrap: "wrap" }}>
          {charger.station_id && charger.station_id !== "—" ? (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A" }}>{charger.station_id}</span>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{charger.charger_sn}</span>
          )}
          <ConfidenceBadge prob={charger.fault_probability} />
          {charger.is_flapping && (
            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: "#E74C3C22", color: "#E74C3C", border: "1px solid #E74C3C66", fontWeight: 700 }}>⚡FLAP</span>
          )}
          {charger.is_remote_resolvable && (
            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", fontWeight: 700 }}>REMOTE RESET</span>
          )}
        </div>
        {charger.site_name && (
          <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{charger.site_name}</div>
        )}
        <div style={{ fontSize: 11, color: "#6B7280" }}>
          {charger.station_type} · P(fault): {(charger.fault_probability * 100).toFixed(1)}% · Score: {charger.priority_score?.toFixed(3)}
          {charger.avg_kwh_day_30d != null && <> · {Math.round(charger.avg_kwh_day_30d)} kWh/day</>}
          {charger.corroboration_score != null && (
            <span style={{ fontWeight: 600, color: charger.corroboration_score >= 4 ? "#EF4444" : charger.corroboration_score >= 2 ? "#F59E0B" : "#9CA3AF" }}>
              {" "}· {charger.corroboration_score}/7 signals
            </span>
          )}
        </div>
        {charger.error_info && (
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            ⚠ {charger.error_info.impact} · {charger.error_info.description}
          </div>
        )}
        {charger.deviation?.is_anomalous && (
          <div style={{ fontSize: 11, color: "#F59E0B", marginTop: 2, fontWeight: 500 }}>
            ↑ {charger.deviation.max_sigma}σ from baseline
          </div>
        )}
        {charger.uptime_30d != null && charger.uptime_30d < 97 && (
          <div style={{ fontSize: 11, color: charger.uptime_30d < 80 ? "#EF4444" : "#D97706", marginTop: 2, fontWeight: 500 }}>
            ⏱ {charger.uptime_30d.toFixed(1)}% uptime{charger.uptime_30d < 80 ? " — critical" : " — below NEVI"}
          </div>
        )}
        {(charger.wo_open ?? 0) > 0 && (
          <div style={{ fontSize: 11, color: "#D97706", marginTop: 2, fontWeight: 500 }}>
            📋 {charger.wo_open} open WO{charger.wo_open !== 1 ? "s" : ""}
            {charger.last_wo_category && <span style={{ color: "#9CA3AF", fontWeight: 400 }}> · {charger.last_wo_category}</span>}
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, color: tc.color, fontFamily: "'DM Mono', monospace", flexShrink: 0, marginTop: 2, fontWeight: 700 }}>
        {(charger.fault_probability * 100).toFixed(0)}%
      </div>
    </div>
  );
};

export const ChartModal = ({ title, onClose, children }) => (
  <div
    onClick={onClose}
    className="dp-modal-overlay dp-themed-area"
    style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.45)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 32,
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      className="dp-modal"
      style={{
        background: "#FFFFFF", borderRadius: 10, padding: 28,
        width: "min(900px, 100%)", maxHeight: "80vh", overflow: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        border: "1px solid #E5E7EB",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", letterSpacing: "0.1em" }}>{title}</span>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: "#9CA3AF", cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>
      {children}
    </div>
  </div>
);
